/**
 *
 * FBAppointment
 *
 */

import React, { memo, useCallback, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form, Dropdown, Button } from "antd";

// custom react-multi-date-picker
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import { monthsFRLowercase, weekDaysFR, monthsDE, weekDaysDE } from "utils/moment/multiDateLocale";
// custom react-multi-date-picker
import { DateObject } from "react-multi-date-picker";

// icons
import CustomIcons from "components/CustomIcons";

// layout components
import ErrorBoundary from "engine/ErrorBoundary";

import {
  FBSectionHeader,
  FBSectionErroMessage,
  FBSectionFieldActions,
} from "components/Communications";

import HiddenField from "components/Forms/Fields/HiddenField";
// inner components
import SectionFields from "./components/SectionFields";

// hooks
import { useRefState } from "utils/hooks";

function FBAppointment({
  name,
  mode,
  method,
  initialValues,
  isValidSection,
  // actions
  setValidSection,
  onDelete,
  onChangeSection,
  // default props
  className,
  ...rest
}) {
  const childClassNames = classNames(
    "tcf-fb-appointments-group",
    "tcf-fb-group",
    className,
  );
  const { t, i18n } = useTranslation();

  let formName = name;

  const [dynamicAppointment] = Form.useForm();

  const [validFields, setValidFields] = useState(isValidSection || false);
  const [currentValues, currentValuesRef, setCurrentValues] = useRefState(
    initialValues || {},
  );

  const formatDateTimeValue = useCallback((value, format) => {
    if (!!!value) {
      return "";
    }

    if (typeof value == "object") {
      return !!value && value.format(format);
    } else {
      return !!value && new DateObject(value).format(format);
    }
  }, []);

  function validateForm(formData, name = "appointments") {
    let isValid = true;
    // code for validation title
    /* const formTitle = formData?.[`${name}-title`] || "";
    const regex = /<[^>]*>|^\s*$/gm; // match any HTML tags and whitespace
    const actualTitle = formTitle?.replace(regex, "")?.replace("\n", ""); // remove any HTML tags and whitespace
    const containsActualTitle = !!formTitle && !!actualTitle && actualTitle?.length > 0; */
    /* if (!containsActualTitle) {
      isValid = false;
    } else  */ if (
      Array.isArray(formData?.[`${name}-fields`]) &&
      formData?.[`${name}-fields`]?.length > 0
    ) {
      for (let field of formData?.[`${name}-fields`]) {
        if (!field?.["field-date"]) {
          isValid = false;
        } else if (!!!field?.["field-type"]) {
          isValid = false;
        } else if (
          field?.["field-type"] === "radio" ||
          field?.["field-type"] === "checkbox"
        ) {
          if (
            !field?.["field-input"] ||
            field?.["field-input"]?.some(
              (input) => !input?.["time-from"] || !input?.["time-to"],
            )
          ) {
            isValid = false;
            break;
          }
        } else if (!!!field?.["field-type"]) {
          isValid = false;
        }
      }
    } else {
      isValid = false;
    }
    return isValid;
  }

  useEffect(() => {
    if (!!initialValues) {
      const isValid = validateForm(initialValues);
      if (!isValid) {
        dynamicAppointment.validateFields();
      }
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      if (JSON.stringify(initialValues) != JSON.stringify(currentValues)) {
        setCurrentValues(initialValues);
      }
    } else if (!!!initialValues) {
      resetAllStates();
    }

    return () => {
      resetAllStates();
    };
  }, []);

  const resetAllStates = useCallback(() => {
    setCurrentValues({});
    setValidFields(false);
    dynamicAppointment.resetFields();
  }, [initialValues, currentValues]);

  // Delete Section
  const deleteSection = useCallback(
    (name) => {
      resetAllStates();
      // update Section
      !!onChangeSection && onChangeSection(name, null);
      !!onDelete && onDelete(name);
    },
    [name, onDelete, onChangeSection],
  );

  // custom Validation for Section fileds
  const checkValidCallback = useCallback(
    async (rule, values, callback) => {
      const isValid =
        validateForm(currentValuesRef?.current) && values?.length > 0;
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);

      if (isValid) {
        return Promise.resolve(isValid);
      } else {
        return Promise.reject(new Error(t("errors.entryNotValid")), values);
      }
    },
    [name, setValidSection, currentValuesRef],
  );

  const erroMessage = useMemo(
    () => (
      <FBSectionErroMessage
        showMessage={!validFields}
        message={t("inputs.sectionNeedsMultipleFields")}
      />
    ),
    [validFields, isValidSection, i18n?.language],
  );

  // Update parent states based on changes inside Form
  const handleValuesChange = useCallback(
    (changedValues, allValues) => {
      let values = allValues[name] || {};
      const isValid = validateForm(values);
      setValidFields(isValid);
      !!setValidSection && setValidSection(name, isValid);
      !!isValid && setCurrentValues(values);
      //  dynamicAppointment.validateFields();
    },
    [name, currentValues, setValidSection],
  );

  /* Generate Filed based on chozen type */
  const generateRadioOrCheckboxFiled = useCallback(
    (element, fieldType, rowIndex) => {
      const { key, name, ...restField } = element;
      return (
        <>
          <Form.List
            {...restField}
            name={[name, "field-input"]}
            key={[key, "field-input"]}
            className={`appointment-field ${fieldType}`}
            rules={[
              {
                required: true,
                validator: async (_, vals) => {
                  if (!!!vals) {
                    return Promise.reject(
                      new Error(t("inputs.mandatoryField")),
                    );
                  }
                },
                message: t("inputs.mandatoryField"),
                validateTrigger: "onChange",
              },
            ]}
          >
            {(fields, { add, remove, move }) => {
              return (
                <>
                  {fields.map((element, index) => {
                    const { key, name, ...restField } = element;
                    return (
                      <div
                        key={`appointment-row-${key}_${index}-key-${rowIndex}-row`}
                        className={`appointment-row appointment-row-${index} ${fieldType}`}
                        align="baseline"
                      >
                        <div className="options-wrapper">
                          <Form.Item
                            {...restField}
                            name={[name, `time-from`]}
                            key={[key, `time-from`]}
                            className="appointment-field"
                            rules={[
                              {
                                required: true,
                                validator: async (_, vals) => {
                                  if (!!!vals) {
                                    return Promise.reject(
                                      new Error(t("inputs.mandatoryField")),
                                    );
                                  }
                                },
                                message: t("inputs.mandatoryField"),
                                validateTrigger: "onChange",
                              },
                            ]}
                          >
                            <DatePicker
                              className="ant-form-item time"
                              scrollSensitive={false}
                              hideOnScroll
                              editable={true}
                              single
                              disableYearPicker
                              disableMonthPicker
                              hideMonth
                              hideYear
                              hideWeekDays
                              arrow={false}
                              rules={{ required: true }}
                              format="HH:mm"
                              weekStartDayIndex={1}
                              plugins={[
                                <TimePicker
                                  position="top"
                                  hideSeconds
                                  style={{ minWidth: "115px" }}
                                />,
                              ]}
                              placeholder={t("inputs.startTime")}
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, `time-to`]}
                            key={[key, `time-to`]}
                            className="appointment-field"
                            rules={[
                              {
                                required: true,
                                validator: async (_, vals) => {
                                  if (!!!vals) {
                                    return Promise.reject(
                                      new Error(t("inputs.mandatoryField")),
                                    );
                                  }
                                },
                                message: t("inputs.mandatoryField"),
                                validateTrigger: "onChange",
                              },
                            ]}
                          >
                            <DatePicker
                              className="ant-form-item time"
                              scrollSensitive={false}
                              hideOnScroll
                              editable={true}
                              single
                              disableYearPicker
                              disableMonthPicker
                              hideMonth
                              hideYear
                              hideSeconds
                              hideWeekDays
                              arrow={false}
                              rules={{ required: true }}
                              format="HH:mm"
                              weekStartDayIndex={1}
                              plugins={[
                                <TimePicker
                                  position="top"
                                  hideSeconds
                                  style={{ minWidth: "115px" }}
                                />,
                              ]}
                              placeholder={t("inputs.endTime")}
                            />
                          </Form.Item>
                        </div>
                        <div className="appointment-row__actions">
                          {fields?.length > 2 && (
                            <CustomIcons
                              className="dynamic-delete-button"
                              type="garbage"
                              onClick={() => remove(name)}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <Button className="add-sub-options" onClick={() => add()}>
                    {t("inputs.addOption")}
                  </Button>
                </>
              );
            }}
          </Form.List>
        </>
      );
    },
    [],
  );

  const getRowsValueType = useCallback(
    (name) => {
      let cur = dynamicAppointment.getFieldsValue();
      let curV =
        Object.keys(cur)?.length > 0 ? cur : { [formName]: currentValues };
      let filedsValues = curV[formName][`${formName}-fields`] || [];
      let rowValsType =
        filedsValues?.length > 0 && filedsValues[name]?.["field-type"];
      return rowValsType;
    },
    [formName, currentValues],
  );

  const generateFileldByType = useCallback(
    (element, rowIndex) => {
      const { key, name, ...restField } = element;
      const rowValsType = getRowsValueType(name);

      switch (rowValsType) {
        case "radio":
          return generateRadioOrCheckboxFiled(element, "radio", rowIndex);
        case "checkbox":
          return generateRadioOrCheckboxFiled(element, "checkbox", rowIndex);
        default:
          return <></>;
      }
    },
    [initialValues, currentValues],
  );

  /* Add new Filed Row */
  const fieldTypeList = [
    { label: t("inputs.multipleChoice"), key: "checkbox" },
    { label: t("inputs.uniqueChoice"), key: "radio" },
  ];
  const addNewTypeField = useCallback(
    (type, addCalback) => {
      let defVals = {
        "field-date": null,
        "field-type": type,
        "field-input": null,
      };
      if (type == "radio") {
        defVals["field-input"] = [
          { [`time-from`]: null, [`time-to`]: null },
          { [`time-from`]: null, [`time-to`]: null },
        ];
      } else if (type == "checkbox") {
        defVals["field-input"] = [
          { [`time-from`]: null, [`time-to`]: null },
          { [`time-from`]: null, [`time-to`]: null },
        ];
      }
      setValidFields(false);
      !!setValidSection && setValidSection(name, false);
      addCalback(defVals);
    },
    [setValidSection, name],
  );
  const dorpdownAddNewRow = useCallback(
    (addCallback) => (
      <div className="add-new-filed__wrapper">
        <Dropdown
          menu={{ items: fieldTypeList }}
          trigger={["hover"]}
          className="add-new-filed"
          overlayClassName="add-new-filed__dropdown"
          getPopupContainer={(trigger) => trigger.parentElement}
          dropdownRender={(menu) => {
            const items = menu.props.items;
            return (
              <div className="dropdown-content">
                {items?.map(({ key, label }) => {
                  return (
                    <div
                      className="dropdown-content__item"
                      onClick={() => addNewTypeField(key, addCallback)}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            );
          }}
          placement="top"
          arrow={{
            pointAtCenter: true,
          }}
        >
          <Button>
            <span className="add-new-filed__btn">{t("inputs.addDay")}</span>
          </Button>
        </Dropdown>
      </div>
    ),
    [addNewTypeField],
  );

  const renderFormList = useCallback(
    (fields, { add, remove, move }) => {
      return (
        <>
          {fields.map((element, subIndex) => {
            const { key, name, ...restField } = element;
            const fieldType =
              currentValues?.[`${formName}-fields`][name]?.["field-type"];
            const groupKey = `${fieldType}-${name}-${subIndex}`;
            return (
              <div
                key={groupKey}
                className="appointment-group"
                align="baseline"
              >
                <FBSectionFieldActions
                  type="appointments"
                  key={`${groupKey}-group-action`}
                  index={name}
                  fieldType={fieldType}
                  fieldTypeList={fieldTypeList}
                  totalLength={currentValues[`${formName}-fields`]?.length || 0}
                  onMove={move}
                  onDelete={() => remove(name)}
                />

                <div className="appointment-group__context">
                  <HiddenField
                    key={[name, `field-type`]}
                    data={{ name: [name, `field-type`] }}
                    index={0}
                  />

                  <Form.Item
                    {...restField}
                    name={[name, "field-date"]}
                    key={[key, "field-date"]}
                    className="appointment-title"
                    rules={[
                      {
                        required: true,
                        message: t("inputs.mandatoryField"),
                      },
                    ]}
                  >
                    <DatePicker
                      className="ant-form-item date"
                      rules={{ required: true }}
                      scrollSensitive={false}
                      hideOnScroll
                      editable={false}
                      format="DD.MM.YYYY"
                      months={i18n?.language == "fr" && monthsFRLowercase || i18n?.language == "de" && monthsDE}
                      weekDays={i18n?.language == "fr" && weekDaysFR || i18n?.language == "de" && weekDaysDE}
                      placeholder={t("inputs.date")}
                      weekStartDayIndex={1}
                      locale={i18n?.language}
                    />
                  </Form.Item>

                  {generateFileldByType(element, subIndex)}
                </div>
              </div>
            );
          })}

          {dorpdownAddNewRow(add)}
        </>
      );
    },
    [i18n?.language],
  );

  return (
    <div className={childClassNames}>
      <Form
        name={formName}
        form={dynamicAppointment}
        initialValues={{ [formName]: currentValues }}
        className="tcf-form-builder"
        autoComplete="off"
        validateTrigger={["onChange", "onSubmit"]}
        onValuesChange={handleValuesChange}
      >
        <FBSectionHeader
          key="appointments-section-header"
          type="appointments"
          onDelete={() => deleteSection(formName)}
        />
        <div className="tcf-fb-group">
          <SectionFields
            key="appointments-section-header"
            name={formName}
            sectionValues={currentValues}
            required={false}
          />
          <ErrorBoundary>
            <Form.List
              className="tcf-fb-group"
              name={[formName, `${formName}-fields`]}
              rules={[
                {
                  required: true,
                  validator: checkValidCallback,
                  validateTrigger: "onChange",
                  message: t("inputs.mandatoryField"),
                },
              ]}
            >
              {(fields, { add, remove, move }) =>
                renderFormList(fields, { add, remove, move })
              }
            </Form.List>
          </ErrorBoundary>
          {erroMessage}
        </div>
      </Form>
    </div>
  );
}

FBAppointment.defaultProps = {
  name: "appointments",
  mode: "new",
  method: "new",
};
FBAppointment.propTypes = {
  onDelete: PropTypes.func,
};

export default memo(FBAppointment);
